import _ from "lodash";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect } from "react";
import classes from "./StudentActivityDetail.scss";
import ScrollableCarousel from "UIComponents/ScrollableCarousel";
import { StudentActivity } from "../StudentActivities/StudentActivities";
import { Tabs, MediaCard, Avatar, Tooltip, Badge } from "@toddle-edu/ds-web";
import { AVATAR_COLORS } from "Constants/colorConstants";
import {
  getStringAutoLinks,
  getStyleStrippedText,
  hashString,
  onEnterOrSpaceKeyDown,
} from "Utils";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getFormattedUserNameWithoutPronoun } from "Post/utils";

const StudentActivityDetail = ({
  studentActivities,
  activitySelectedStudentId,
  handleActivitySelectedStudentAttachmentsChange,
  theatreActiveAttachmentIndex,
  setTheatreAttachmentIndex,
  leftButtonStyle = {},
  rightButtonStyle = {},
  handleActivitySelectedStudentIdChange,
  defaultStudentNameOrderPreference,
}) => {
  const getSelectedStudentAttachments = selectedStudentActivityData => {
    let allAttachments = [];

    if (!_.isNil(selectedStudentActivityData)) {
      const attachmentsGroups = _.get(
        selectedStudentActivityData,
        "attachmentGroups",
        []
      );
      _.forEach(attachmentsGroups, group => {
        const attachments = _.get(group, "attachments", []);
        allAttachments = _.concat(allAttachments, attachments);
      });
    }
    return allAttachments;
  };

  const initialStudentId = !_.isEmpty(activitySelectedStudentId)
    ? activitySelectedStudentId
    : _.get(studentActivities, "0.item.activityStudent.id", "");

  const selectedStudentActivityData = _.get(
    _.find(
      studentActivities,
      activity =>
        _.get(activity, "item.activityStudent.id") === initialStudentId
    ),
    "item",
    {}
  );
  const attachments = getSelectedStudentAttachments(
    selectedStudentActivityData
  );

  const [shouldScrollToId, setShouldScrollToId] = useState(false);

  const scrollableContainerRef = useRef(null);

  const tabs = _.map(studentActivities, activityData => {
    const id = _.get(activityData, "item.activityStudent.id", "");
    const firstName = _.get(activityData, "item.activityStudent.firstName", "");
    const lastName = _.get(activityData, "item.activityStudent.lastName", "");
    const profileImage = _.get(
      activityData,
      "item.activityStudent.profileImage",
      ""
    );

    const studentName = getFormattedUserName({
      user: _.get(activityData, "item.activityStudent", {}),
    });
    const studentNameForAvatar = getFormattedUserNameWithoutPronoun({
      user: _.get(activityData, "item.activityStudent", {}),
    });

    return {
      value: `activityDetail:${id}`,
      suffix: (
        <Tooltip tooltip={studentName}>
          <span className={classes.tabLabel}>{studentName}</span>
        </Tooltip>
      ),
      customComponent: (
        <ConditionalWrapper
          condition={
            _.get(activityData, ["item", "comments", "unreadCountV2"], 0) > 0
          }
          wrapper={
            <Badge
              type="dot"
              size="large"
              dotBadgeColorVariant="critical"
              offset={[4, 4]}
            />
          }
          type={"wrapperAsElement"}
        >
          <Avatar
            shade={"light"}
            size={"small"}
            color={AVATAR_COLORS[hashString(id) % AVATAR_COLORS.length]}
            name={
              _.size(defaultStudentNameOrderPreference) === 3
                ? `${firstName} ${lastName}`
                : studentNameForAvatar
            }
            src={profileImage}
          />
        </ConditionalWrapper>
      ),
    };
  });

  const handleStudentTabChange = value => {
    const id = _.get(_.split(value, ":"), "1", "");
    const nextSelectedStudentActivityData = _.get(
      _.find(
        studentActivities,
        activity => _.get(activity, "item.activityStudent.id") === id
      ),
      "item",
      {}
    );

    if (_.isFunction(handleActivitySelectedStudentAttachmentsChange)) {
      const nextAttachments = getSelectedStudentAttachments(
        nextSelectedStudentActivityData
      );
      handleActivitySelectedStudentAttachmentsChange(
        setTheatreAttachmentIndex,
        nextAttachments
      );
    }

    if (_.isFunction(setTheatreAttachmentIndex)) setTheatreAttachmentIndex(0);
    handleActivitySelectedStudentIdChange(id);
  };

  const getScrollableContainerRef = ref =>
    (scrollableContainerRef.current = ref?.current);

  // i know this is kinda hackie but didn't got any other ideas to make the component scroll into view
  // https://github.com/facebook/react/issues/23396#issuecomment-1460074001
  useEffect(() => {
    if (!shouldScrollToId || _.isNil(scrollableContainerRef.current)) return;
    const tabIndex = _.findIndex(
      tabs,
      tab => tab.value === `activityDetail:${activitySelectedStudentId}`
    );

    if (tabIndex === -1) return;

    const tabComponents = scrollableContainerRef.current.children[0].children;
    setTimeout(() => {
      tabComponents[tabIndex].scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }, 100);
    setShouldScrollToId(false);
  }, [shouldScrollToId]);

  useEffect(() => {
    if (
      _.isEmpty(activitySelectedStudentId) ||
      _.isNil(scrollableContainerRef.current)
    )
      return;

    const tabIndex = _.findIndex(
      tabs,
      tab => tab.value === `activityDetail:${activitySelectedStudentId}`
    );

    if (tabIndex === -1) return;

    const tabComponents = _.get(
      scrollableContainerRef.current,
      "children.0.children"
    );
    if (!_.isNil(tabComponents) && !_.isNil(tabComponents[tabIndex]))
      setShouldScrollToId(true);
  }, [scrollableContainerRef]);

  useEffect(() => {
    if (!_.isFunction(handleActivitySelectedStudentAttachmentsChange)) return;

    handleActivitySelectedStudentAttachmentsChange(
      setTheatreAttachmentIndex,
      attachments
    );
  }, []);

  const onTheatreAttachmentKeydown = index => e => {
    onEnterOrSpaceKeyDown(e, () => setTheatreAttachmentIndex?.(index));
  };

  const onTheatreAttachmentClick = index => e => {
    setTheatreAttachmentIndex?.(index);
  };

  return (
    <>
      {_.size(tabs) > 1 ? (
        <ScrollableCarousel
          getScrollableContainerRef={getScrollableContainerRef}
          scrollAmount={89 * 3}
          chevronIconSize={"xxx-small"}
          leftButtonStyle={
            !_.isEmpty(leftButtonStyle)
              ? leftButtonStyle
              : {
                  left: 0,
                  width: "24px",
                  height: "24px",
                  transform: "translateY(-156%)",
                }
          }
          rightButtonStyle={
            !_.isEmpty(rightButtonStyle)
              ? rightButtonStyle
              : {
                  right: 0,
                  width: "24px",
                  height: "24px",
                  transform: "translateY(-156%)",
                }
          }
        >
          <div className={classes.tabContainer}>
            <Tabs
              options={tabs}
              value={`activityDetail:${initialStudentId}`}
              onChange={handleStudentTabChange}
              type={"flexible"}
              containerStyle={{ height: "36px", width: "76px" }}
              testId="journal-feed-student-activity-tabs"
            />
          </div>
        </ScrollableCarousel>
      ) : null}
      {!_.isEmpty(selectedStudentActivityData) ? (
        <div className={classes.studentActivityDetailContainer}>
          {!_.isEmpty(_.get(selectedStudentActivityData, "description")) ? (
            <p
              dangerouslySetInnerHTML={{
                __html: getStyleStrippedText({
                  text: getStringAutoLinks(
                    _.get(selectedStudentActivityData, "description")
                  ),
                  type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                }),
              }}
              className={classes.description}
            />
          ) : null}
          {!_.isEmpty(attachments) ? (
            <div className={classes.attachmentsContainer}>
              {_.map(attachments, (attachment, index) => (
                <div
                  tabIndex={0}
                  className={classes.mediaCardContainer}
                  onKeyDown={onTheatreAttachmentKeydown(index)}
                >
                  <MediaCard
                    key={_.get(attachment, "id", "")}
                    cardType={"medium"}
                    name={_.get(attachment, "name", "")}
                    type={_.get(attachment, "type", "")}
                    size={_.get(attachment, "metadata.size", 0)}
                    mimeType={_.get(attachment, "mimeType")}
                    url={_.get(attachment, "url", "")}
                    thumbUrl={
                      _.get(attachment, "thumbUrl", "") ||
                      _.get(attachment, "url", "")
                    }
                    isActivated={theatreActiveAttachmentIndex === index}
                    showActionsOnHover={false}
                    onClick={onTheatreAttachmentClick?.(index)}
                    testId="journal-feed-postcard-mediacard"
                  />
                </div>
              ))}
            </div>
          ) : null}
          {!_.isEmpty(
            _.get(selectedStudentActivityData, "ibPYPElementRatings")
          ) ||
          !_.isEmpty(_.get(selectedStudentActivityData, "startTime", "")) ||
          !_.isEmpty(_.get(selectedStudentActivityData, "endTime", "")) ? (
            <StudentActivity
              activityData={selectedStudentActivityData}
              isSingleActivity={true}
              isSidePaneContent={true}
              activityContianerStyle={{ gap: "8px", paddingInline: "16px" }}
              defaultStudentNameOrderPreference={
                defaultStudentNameOrderPreference
              }
            />
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default StudentActivityDetail;
